<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { site } from '@/i18n'
import UmbBootLogo from '@/components/UmbBootLogo.vue'
import LangSwitch from '@/components/LangSwitch.vue'

const route = useRoute()
const router = useRouter()
const isScrolled = ref(false)
const mobileOpen = ref(false)
const productOpen = ref(false)
let productTimeout: number | null = null

const currentPath = computed(() => route.path)

function go(path: string) {
  router.push(path)
  mobileOpen.value = false
  productOpen.value = false
}

function handleScroll() {
  isScrolled.value = window.scrollY > 20
}

function openProduct() {
  if (productTimeout) clearTimeout(productTimeout)
  productOpen.value = true
}

function closeProduct() {
  productTimeout = window.setTimeout(() => {
    productOpen.value = false
  }, 120)
}

onMounted(() => {
  window.addEventListener('scroll', handleScroll, { passive: true })
  handleScroll()
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
  if (productTimeout) clearTimeout(productTimeout)
})
</script>

<template>
  <header class="site-header" :class="{ 'is-scrolled': isScrolled }">
    <div class="container header-inner">
      <RouterLink to="/" class="brand">
        <UmbBootLogo class="brand-icon" :size="36"/>
        <span class="brand-text">{{ site.ui.brand }}<span class="brand-suffix">{{ site.ui.brandSuffix }}</span></span>
      </RouterLink>

      <nav class="desktop-nav">
        <div
          class="nav-item has-dropdown"
          :class="{ active: currentPath === '/product' }"
          @mouseenter="openProduct"
          @mouseleave="closeProduct"
        >
          <button class="nav-link" @click="go('/product')">
            {{ site.nav.items[0].label }}
            <svg class="dropdown-chevron" :class="{ open: productOpen }" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </button>
          <Transition name="dropdown">
            <div v-show="productOpen" class="dropdown">
              <RouterLink
                v-for="item in site.nav.productDropdown"
                :key="item.anchor"
                :to="`/product#${item.anchor}`"
                class="dropdown-link"
              >
                {{ item.label }}
              </RouterLink>
            </div>
          </Transition>
        </div>

        <RouterLink
          v-for="item in site.nav.items.slice(1)"
          :key="item.path"
          :to="item.path"
          class="nav-link"
          :class="{ active: currentPath === item.path }"
        >
          {{ item.label }}
        </RouterLink>
      </nav>

      <div class="header-actions">
        <LangSwitch/>
        <button class="btn btn-secondary login-btn" @click="go('/login')">
          {{ site.nav.login }}
        </button>
        <button class="btn btn-primary cta-btn" @click="go('/login')">
          {{ site.nav.cta }}
        </button>
      </div>

      <button class="burger" :class="{ open: mobileOpen }" @click="mobileOpen = !mobileOpen" :aria-label="site.ui.menu" :aria-expanded="mobileOpen">
        <span/><span/><span/>
      </button>
    </div>

    <Transition name="drawer">
      <div v-show="mobileOpen" class="mobile-drawer">
        <div class="mobile-nav">
          <RouterLink to="/product" class="mobile-link" @click="mobileOpen = false">{{ site.nav.items[0].label }}</RouterLink>
          <RouterLink
            v-for="item in site.nav.items.slice(1)"
            :key="item.path"
            :to="item.path"
            class="mobile-link"
            @click="mobileOpen = false"
          >
            {{ item.label }}
          </RouterLink>
          <div class="mobile-actions">
            <button class="btn btn-secondary" @click="go('/login')">{{ site.nav.login }}</button>
            <button class="btn btn-primary" @click="go('/login')">{{ site.nav.cta }}</button>
          </div>
          <div class="mobile-lang">
            <LangSwitch/>
          </div>
        </div>
      </div>
    </Transition>
  </header>
</template>

<style scoped>
.site-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  height: var(--header-height);
  transition: background 0.3s ease, backdrop-filter 0.3s ease, box-shadow 0.3s ease;
  border-bottom: 1px solid transparent;
}

.site-header.is-scrolled {
  background: rgba(255, 255, 255, 0.88);
  backdrop-filter: blur(12px);
  border-bottom-color: var(--u2-stroke);
  box-shadow: var(--shadow-sm);
}

.header-inner {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-4);
}

.brand {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  font-weight: 900;
  font-size: 20px;
  letter-spacing: -0.02em;
  white-space: nowrap;
  flex-shrink: 0;
}

.brand-icon {
  flex-shrink: 0;
}

.brand-suffix {
  color: var(--u2-primary);
  margin-left: 2px;
}

.desktop-nav {
  display: flex;
  align-items: center;
  gap: 2px;
  flex-wrap: nowrap;
  min-width: 0;
}

.nav-item {
  position: relative;
  flex-shrink: 0;
}

.nav-link {
  display: flex;
  align-items: center;
  gap: var(--space-1);
  padding: var(--space-2);
  border-radius: var(--radius-md);
  color: var(--u2-muted);
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  transition: color 0.2s ease, background 0.2s ease;
  background: transparent;
  border: none;
  cursor: pointer;
}

.nav-link:hover,
.nav-link.active {
  color: var(--u2-text);
  background: var(--u2-hover);
}

.dropdown-chevron {
  width: 14px;
  height: 14px;
  transition: transform 0.25s ease;
}

.dropdown-chevron.open {
  transform: rotate(180deg);
}

.dropdown {
  position: absolute;
  top: calc(100% + 10px);
  left: 50%;
  transform: translateX(-50%);
  min-width: 180px;
  background: var(--u2-elevated);
  border: 1px solid var(--u2-stroke);
  border-radius: var(--radius-lg);
  padding: var(--space-2);
  box-shadow: var(--shadow-lg);
}

.dropdown-link {
  display: block;
  padding: var(--space-3) var(--space-4);
  border-radius: var(--radius-md);
  color: var(--u2-muted);
  font-size: 14px;
  transition: all 0.2s ease;
}

.dropdown-link:hover {
  color: var(--u2-text);
  background: rgba(var(--u2-primary-rgb), 0.06);
}

.dropdown-enter-active,
.dropdown-leave-active {
  transition: opacity 0.2s ease, transform 0.2s var(--ease-out-expo);
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(-8px);
}

.header-actions {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  flex-shrink: 0;
}

.login-btn {
  padding: var(--space-2) var(--space-4);
  white-space: nowrap;
}

.cta-btn {
  padding: var(--space-2) var(--space-4);
  white-space: nowrap;
}

.burger {
  display: none;
  flex-direction: column;
  justify-content: center;
  gap: 5px;
  width: 40px;
  height: 40px;
  background: transparent;
  border: none;
  cursor: pointer;
}

.burger span {
  display: block;
  width: 24px;
  height: 2px;
  background: var(--u2-text);
  border-radius: 2px;
  transition: transform 0.25s ease, opacity 0.25s ease;
  transform-origin: center;
}

.burger.open span:nth-child(1) {
  transform: translateY(7px) rotate(45deg);
}
.burger.open span:nth-child(2) {
  opacity: 0;
}
.burger.open span:nth-child(3) {
  transform: translateY(-7px) rotate(-45deg);
}

.mobile-drawer {
  display: none;
  position: absolute;
  top: var(--header-height);
  left: 0;
  right: 0;
  background: var(--u2-elevated);
  border-bottom: 1px solid var(--u2-stroke);
  box-shadow: var(--shadow-md);
  padding: var(--space-4);
}

.mobile-nav {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.mobile-link {
  padding: var(--space-3) var(--space-4);
  border-radius: var(--radius-md);
  color: var(--u2-muted);
  font-size: 16px;
  font-weight: 500;
}

.mobile-link:hover,
.mobile-link.router-link-active {
  color: var(--u2-text);
  background: var(--u2-hover);
}

.mobile-actions {
  display: flex;
  gap: var(--space-3);
  margin-top: var(--space-4);
}

.mobile-actions .btn {
  flex: 1;
}

.mobile-lang {
  margin-top: var(--space-3);
}

.drawer-enter-active,
.drawer-leave-active {
  transition: opacity 0.25s ease, transform 0.25s var(--ease-out-expo);
}

.drawer-enter-from,
.drawer-leave-to {
  opacity: 0;
  transform: translateY(-12px);
}

@media (max-width: 1180px) {
  .desktop-nav,
  .header-actions {
    display: none;
  }
  .burger,
  .mobile-drawer {
    display: flex;
  }
}
</style>
